/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Notes:
 * - A slice's content is a fragment
 * - A fragment is like an array of nodes
 * - A node's content is a fragment
 */
import { Slice, Node, Fragment } from 'prosemirror-model';

const MAX_SRC_LENGTH = 1024;

function createUniquePrefix() {
  // First character [a-z]
  let firstChar = Math.floor(Math.random() * 26 + 10).toString(36);
  // Additional characters [0-9a-z]
  let nextFour = Math.floor(Math.random() * 36 ** 4).toString(36);
  return firstChar + nextFour;
}

function createClassNameProcessor() {
  let uniquePrefix = createUniquePrefix();
  return (name: string) => uniquePrefix + '-' + name.toLowerCase();
}

export function createClassNames<T extends ObjectOf<true>>(classNames: T) {
  let cx = createClassNameProcessor();
  let names = Object.keys(classNames);
  let result = Object.fromEntries(names.map((name) => [name, cx(name)]));
  return result as { [K in keyof T]: string };
}

function isValidImage(node: Node<any>) {
  const isImage = node.type.name === 'image';
  const src = node.attrs.src ?? '';
  const hasDataPrefix = src.startsWith('data:');
  const isValidSrcLength = src.length <= MAX_SRC_LENGTH;
  return isImage && !hasDataPrefix && isValidSrcLength;
}

function cleanNode(node: Node<any>) {
  // Return null for invalid images
  if (node.type.name === 'image' && !isValidImage(node)) {
    return null;
  }
  // base case: for a leaf node, return itself
  if (node.content.childCount === 0) {
    return node;
  }
  let newContent = cleanFragment(node.content);
  // If no changes were made, just return the same node
  return newContent === node.content ? node : node.copy(newContent);
}

function cleanFragment(fragment: Fragment<any>) {
  let isDirty = false;
  let cleaned: Array<Node<any>> = [];
  fragment.forEach((node) => {
    let newNode = cleanNode(node);
    if (newNode !== node) {
      isDirty = true;
    }
    if (newNode != null) {
      cleaned.push(newNode);
    }
  });
  return isDirty ? Fragment.from(cleaned) : fragment;
}

export function cleanPasteFromSlice(slice: Slice<any>) {
  let fragment = cleanFragment(slice.content);
  return new Slice(fragment, slice.openStart, slice.openEnd);
}
